import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Chart from 'react-apexcharts';
import { tradierService } from '../../services';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import type { ChartData } from '../../types/market';

/** Lightweight date formatter replacing date-fns `formatDate()` for the patterns used in this file. */
function formatDate(date: Date, pattern: string): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const h24 = date.getHours();
  const h12 = h24 % 12 || 12;
  const ampm = h24 < 12 ? 'AM' : 'PM';
  return pattern
    .replace('MMM dd, yyyy', `${months[date.getMonth()]} ${pad(date.getDate())}, ${date.getFullYear()}`)
    .replace('MMM yyyy',     `${months[date.getMonth()]} ${date.getFullYear()}`)
    .replace('MMM dd',       `${months[date.getMonth()]} ${pad(date.getDate())}`)
    .replace('HH:mm',        `${pad(h24)}:${pad(date.getMinutes())}`)
    .replace('h:mm a',       `${h12}:${pad(date.getMinutes())} ${ampm}`)
    .replace('yyyy',         `${date.getFullYear()}`);
}

interface ApexStockChartProps {
  symbol: string;
  className?: string;
}

type TimeRange = '1D' | '5D' | '1Y' | '5Y' | 'Max';

interface ComparisonPoint {
  x: number;
  y: number;
  timestamp: string;
  price: number;
}

const ApexStockChart: React.FC<ApexStockChartProps> = ({ symbol, className = '' }) => {
  const [chartData, setChartData] = useState<ChartData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRange, setSelectedRange] = useState<TimeRange>('1D');
  const [selectedPoint, setSelectedPoint] = useState<ComparisonPoint | null>(null);
  const [previousClose, setPreviousClose] = useState<number | null>(null);

  const timeRanges: { value: TimeRange; label: string }[] = [
    { value: '1D', label: '1D' },
    { value: '5D', label: '5D' },
    { value: '1Y', label: '1Y' },
    { value: '5Y', label: '5Y' },
    { value: 'Max', label: 'Max' },
  ];

  // Fetch historical data
  const fetchHistoricalData = useCallback(async (range: TimeRange) => {
    try {
      setLoading(true);
      setError(null);
      setSelectedPoint(null);
      setPreviousClose(null);

      const data = await tradierService.instance.getHistoricalData(symbol, range);
      setChartData(data);

      // For 1D chart, get previous day's closing price
      if (range === '1D' && data.data.length > 0) {
        try {
          // Get 5D data to find previous day's close
          const historicalData = await tradierService.instance.getHistoricalData(symbol, '5D');
          if (historicalData.data.length >= 2) {
            // Get the second-to-last day's closing price
            const prevClose = historicalData.data[historicalData.data.length - 2].close;
            setPreviousClose(prevClose);
          }
        } catch (err) {
          console.warn('Could not fetch previous day closing price:', err);
          // Fallback to first data point of current day
          setPreviousClose(data.data[0].close);
        }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch chart data';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [symbol]);

  // Initial data fetch and range changes
  useEffect(() => {
    if (symbol) {
      fetchHistoricalData(selectedRange);
    }
  }, [symbol, selectedRange, fetchHistoricalData]);

  // Prepare chart series data
  const chartSeries = useMemo(() => {
    if (!chartData?.data.length) return [];

    let seriesData = chartData.data.map((point) => ({
      x: selectedRange === '1D'
        ? new Date(`${point.timestamp}`).getTime()
        : new Date(point.timestamp).getTime(),
      y: point.close
    }));

    // For 1D chart, filter data to only show up to current time
    if (selectedRange === '1D') {
      const now = new Date();
      seriesData = seriesData.filter(point => point.x <= now.getTime());
    }

    return [{
      name: `${symbol} Price`,
      data: seriesData
    }];
  }, [chartData, symbol, selectedRange]);

  // Calculate price difference for comparison
  const calculatePriceDifference = useCallback((point1: ComparisonPoint, point2: ComparisonPoint) => {
    const absolute = point2.price - point1.price;
    const percentage = ((absolute / point1.price) * 100);
    return { absolute, percentage };
  }, []);

  // Chart options
  const chartOptions = useMemo(() => ({
    chart: {
      type: 'line' as const,
      height: 400,
      background: '#111827',
      foreColor: '#64748b',
      zoom: {
        enabled: false,
      },
      toolbar: {
        show: true,
        tools: {
          download: true,
          selection: false,
          zoom: false,
          zoomin: false,
          zoomout: false,
          pan: false,
          reset: false,
        },
      },
      events: {
        dataPointSelection: (_event: any, _chartContext: any, config: any) => {
          if (chartData?.data[config.dataPointIndex]) {
            const point = chartData.data[config.dataPointIndex];
            const newSelectedPoint: ComparisonPoint = {
              x: config.dataPointIndex,
              y: point.close,
              timestamp: point.timestamp,
              price: point.close
            };
            setSelectedPoint(newSelectedPoint);
          }
        },
        click: (_event: any, _chartContext: any, config: any) => {
          if (config.dataPointIndex !== undefined && chartData?.data[config.dataPointIndex]) {
            const point = chartData.data[config.dataPointIndex];
            const newSelectedPoint: ComparisonPoint = {
              x: config.dataPointIndex,
              y: point.close,
              timestamp: point.timestamp,
              price: point.close
            };
            setSelectedPoint(newSelectedPoint);
          }
        },
        markerClick: (_event: any, _chartContext: any, { dataPointIndex }: any) => {
          if (chartData?.data[dataPointIndex]) {
            const point = chartData.data[dataPointIndex];
            const newSelectedPoint: ComparisonPoint = {
              x: dataPointIndex,
              y: point.close,
              timestamp: point.timestamp,
              price: point.close
            };
            setSelectedPoint(newSelectedPoint);
          }
        },
        mouseLeave: () => {
          setSelectedPoint(null);
        }
      }
    },
    stroke: {
      curve: 'smooth' as const,
      width: 2,
    },
    colors: ['#3b82f6'],
    grid: {
      borderColor: '#1e2d45',
      strokeDashArray: 3,
    },
    xaxis: {
      type: 'datetime' as const,
      labels: {
        formatter: (value: any) => {
          const date = new Date(value);
          if (selectedRange === '1D') {
            return formatDate(date, 'h:mm a');
          } else if (selectedRange === '5D') {
            return formatDate(date, 'MMM dd');
          } else if (selectedRange === '1Y') {
            return formatDate(date, 'MMM yyyy');
          } else if (selectedRange === '5Y' || selectedRange === 'Max') {
            return formatDate(date, 'yyyy');
          } else {
            return formatDate(date, 'MMM dd');
          }
        },
        rotate: selectedRange === '5Y' || selectedRange === 'Max' ? -45 : 0,
        maxHeight: 60,
        style: { colors: '#475569', fontSize: '11px' },
      },
      tickAmount: selectedRange === 'Max' ? 10 : selectedRange === '5Y' ? 5 : undefined,
      tooltip: {
        enabled: false,
      },
      axisBorder: { show: false },
      axisTicks: { color: '#1e2d45' },
      // For 1D chart, show more ticks for better time resolution
      ...(selectedRange === '1D' ? {
        tickAmount: 8, // Show more ticks to display actual data timestamps
      } : {}),
    },
    yaxis: {
      title: {
        text: 'Price ($)',
        style: { color: '#334155', fontSize: '11px' },
      },
      labels: {
        formatter: (value: any) => `$${value.toFixed(2)}`,
        style: { colors: '#475569', fontSize: '11px' },
      },
    },
    tooltip: {
      enabled: true,
      custom: ({ series, seriesIndex, dataPointIndex }: any) => {
        if (!chartData?.data[dataPointIndex]) return '';

        const point = chartData.data[dataPointIndex];
        const price = series[seriesIndex][dataPointIndex];
        const date = new Date(point.timestamp);

        let tooltipContent = `
          <div style="background: #111827; padding: 10px 12px; border: 1px solid #1e2d45; border-radius: 8px; box-shadow: 0 8px 24px rgba(0,0,0,0.5); font-size: 12px; font-family: 'JetBrains Mono', monospace;">
            <div style="font-weight: 700; color: #f1f5f9; margin-bottom: 3px; letter-spacing: -0.02em;">
              $${price.toFixed(2)}
            </div>
        `;

        if (selectedRange === '1D') {
          tooltipContent += `
            <div style="font-size: 11px; color: #64748b;">
              ${formatDate(date, 'HH:mm')}
            </div>
          `;
        } else {
          tooltipContent += `
            <div style="font-size: 11px; color: #64748b;">
              ${formatDate(date, 'MMM dd, yyyy')}
            </div>
          `;
        }

        // Add range info if selected point exists
        if (selectedPoint) {
          const selectedDate = new Date(selectedPoint.timestamp);
          const currentDate = date;

          // Determine which is start and which is end
          const isCurrentAfterSelected = currentDate.getTime() > selectedDate.getTime();
          const startDate = isCurrentAfterSelected ? selectedDate : currentDate;
          const endDate = isCurrentAfterSelected ? currentDate : selectedDate;

          if (selectedRange === '1D') {
            tooltipContent += `
              <div style="font-size: 10px; color: #475569; margin-top: 2px;">
                ${formatDate(startDate, 'HH:mm')} - ${formatDate(endDate, 'HH:mm')}
              </div>
            `;
          } else {
            tooltipContent += `
              <div style="font-size: 10px; color: #475569; margin-top: 2px;">
                ${formatDate(startDate, 'MMM dd, yyyy')} - ${formatDate(endDate, 'MMM dd, yyyy')}
              </div>
            `;
          }
        }

        // Add comparison info if selected point exists and this is a different point
        if (selectedPoint && selectedPoint.x !== dataPointIndex) {
          const currentPoint = {
            x: dataPointIndex,
            y: price,
            timestamp: point.timestamp,
            price: price
          };
          const diff = calculatePriceDifference(selectedPoint, currentPoint);
          const diffColor = diff.absolute >= 0 ? '#059669' : '#dc2626';
          const diffSign = diff.absolute >= 0 ? '+' : '';

          tooltipContent += `
            <div style="margin-top: 5px; padding-top: 5px; border-top: 1px solid #1e2d45;">
              <div style="font-size: 11px; color: ${diffColor}; font-weight: 700; font-family: 'JetBrains Mono', monospace;">
                ${diffSign}$${Math.abs(diff.absolute).toFixed(2)} (${diffSign}${diff.percentage.toFixed(2)}%)
              </div>
            </div>
          `;
        }

        tooltipContent += '</div>';
        return tooltipContent;
      },
    },
    markers: {
      size: 0,
      hover: {
        size: 0,
      },
    },
    annotations: {
      points: [
        ...(selectedPoint ? [{
          x: new Date(selectedPoint.timestamp).getTime(),
          y: selectedPoint.price,
          marker: {
            size: 10,
            fillColor: '#ef4444',
            strokeColor: '#111827',
            strokeWidth: 3,
            shape: 'circle',
          },
        }] : []),
      ],
      xaxis: [
        ...(selectedPoint ? [{
          x: new Date(selectedPoint.timestamp).getTime(),
          strokeDashArray: 4,
          borderColor: '#ef4444',
          borderWidth: 2,
          opacity: 0.8,
          label: {
            text: `$${selectedPoint.price.toFixed(2)}`,
            style: {
              background: '#ef4444',
              color: '#ffffff',
              fontSize: '11px',
              fontWeight: 'bold',
            },
            orientation: 'horizontal',
            offsetY: 10,
          },
        }] : []),
      ],
      yaxis: [
        // Add horizontal line for previous day's closing price (1D only)
        ...(selectedRange === '1D' && previousClose !== null ? [{
          y: previousClose,
          strokeDashArray: 2,
          borderColor: '#334155',
          borderWidth: 1,
          opacity: 0.7,
          label: {
            text: `Prev Close: $${previousClose.toFixed(2)}`,
            style: {
              background: '#1e2d45',
              color: '#94a3b8',
              fontSize: '10px',
              fontWeight: 'normal',
            },
            position: 'left',
            offsetX: 10,
          },
        }] : []),
      ],
    },
  }), [chartData, selectedRange, selectedPoint, calculatePriceDifference]);

  const handleRangeChange = (range: TimeRange) => {
    setSelectedRange(range);
  };

  const handleRefresh = () => {
    fetchHistoricalData(selectedRange);
  };

  const clearComparison = () => {
    setSelectedPoint(null);
  };

  if (loading) {
    return (
      <div className={`${className}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 360, background: '#111827', border: '1px solid #1e2d45', borderRadius: 12 }}>
        <LoadingSpinner size="lg" text="Loading chart data…" />
      </div>
    );
  }

  if (error || !chartSeries.length) {
    return (
      <div className={`${className}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 360, background: '#111827', border: '1px solid #1e2d45', borderRadius: 12 }}>
        <div style={{ textAlign: 'center', maxWidth: 320 }}>
          <div style={{ width: 52, height: 52, borderRadius: '50%', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem' }}>
            <svg width="22" height="22" fill="none" stroke="#f87171" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M12 8v4m0 4h.01" strokeLinecap="round"/></svg>
          </div>
          <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#f1f5f9', marginBottom: '0.5rem' }}>Unable to Load Chart</h3>
          <p style={{ fontSize: '0.8125rem', color: '#475569', marginBottom: '1rem' }}>{error}</p>
          <button onClick={handleRefresh} style={{ background: '#3b82f6', color: '#fff', padding: '0.5rem 1.25rem', borderRadius: 8, fontSize: '0.875rem', fontWeight: 600, border: 'none', cursor: 'pointer' }}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={className} style={{ background: '#111827', borderRadius: 12, overflow: 'hidden' }}>
      {/* ── Chart header ── */}
      <div style={{ display: 'flex', flexDirection: 'column' as const, gap: '0.75rem', padding: '0.875rem 1rem', borderBottom: '1px solid #1e2d45' }}>
        {/* Top row */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' as const, gap: '0.5rem' }}>
          {/* Time range selector */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
            {timeRanges.map(r => {
              const active = selectedRange === r.value;
              return (
                <button
                  key={r.value}
                  onClick={() => handleRangeChange(r.value)}
                  style={{
                    padding: '0.3rem 0.65rem', borderRadius: 7,
                    fontSize: '0.78rem', fontWeight: active ? 700 : 500,
                    background: active ? 'rgba(59,130,246,0.15)' : 'transparent',
                    border: `1px solid ${active ? 'rgba(59,130,246,0.35)' : 'transparent'}`,
                    color: active ? '#60a5fa' : '#475569',
                    cursor: 'pointer', transition: 'all 0.18s',
                  }}
                  onMouseEnter={e => { if (!active) { e.currentTarget.style.background = '#1a2235'; e.currentTarget.style.color = '#94a3b8'; } }}
                  onMouseLeave={e => { if (!active) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#475569'; } }}
                >
                  {r.label}
                </button>
              );
            })}
          </div>

          {/* Right actions */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            {selectedPoint && (
              <button
                onClick={clearComparison}
                style={{ padding: '0.3rem 0.65rem', borderRadius: 7, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', color: '#f87171', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', transition: 'all 0.18s' }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.18)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.1)'; }}
              >
                Clear selection
              </button>
            )}
            <button
              onClick={handleRefresh}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: 7, background: 'transparent', border: '1px solid #1e2d45', cursor: 'pointer', color: '#334155', transition: 'all 0.18s' }}
              title="Refresh chart"
              onMouseEnter={e => { e.currentTarget.style.background = '#1a2235'; e.currentTarget.style.color = '#60a5fa'; e.currentTarget.style.borderColor = 'rgba(59,130,246,0.3)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#334155'; e.currentTarget.style.borderColor = '#1e2d45'; }}
            >
              <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/></svg>
            </button>
          </div>
        </div>

        {/* Selection info */}
        {selectedPoint ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.78rem' }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#ef4444', flexShrink: 0 }} />
            <span style={{ color: '#64748b' }}>Selected:</span>
            <span style={{ color: '#f1f5f9', fontWeight: 700, fontFamily: "'JetBrains Mono', monospace" }}>
              ${selectedPoint.price.toFixed(2)}
            </span>
            <span style={{ color: '#334155' }}>
              {selectedRange === '1D'
                ? formatDate(new Date(selectedPoint.timestamp), 'HH:mm')
                : formatDate(new Date(selectedPoint.timestamp), 'MMM dd, yyyy')}
            </span>
          </div>
        ) : (
          <p style={{ fontSize: '0.72rem', color: '#1e3a5f' }}>
            Click any point to start price comparison
          </p>
        )}
      </div>

      {/* ── Chart ── */}
      <div style={{ padding: '0.5rem 0' }}>
        <Chart options={chartOptions} series={chartSeries} type="line" height={380} />
      </div>

      {/* ── Footer ── */}
      <div style={{ padding: '0.5rem 1rem', borderTop: '1px solid #0f1929', textAlign: 'center' }}>
        <p style={{ fontSize: '0.68rem', color: '#1e3a5f' }}>
          Click a point to select · Hover to compare · Move away to clear
        </p>
      </div>
    </div>
  );
};

export default ApexStockChart;